import type { ModelKey } from '@view-model/domain/key';
import type { StickyModelContentsJSON } from '@view-model/domain/model/model-contents/StickyModelContents';
import { ModelCollection } from '@view-model/domain/model/ModelCollection';
import { StickyModelContents } from '@view-model/domain/model/model-contents/StickyModelContents';
import type { AssetUrlMap } from '@view-model/domain/view-model';
import type { FileContainerAssetCopy } from '@view-model/models/sticky/FileContainer/domain/FileContainer';
import type { ViewModelAssetId } from 'balus-common';

export class ModelContentsCollection {
  private readonly models: StickyModelContents[];

  constructor(models: StickyModelContents[]) {
    this.models = models;
  }

  list(): StickyModelContents[] {
    return [...this.models];
  }

  dump(): StickyModelContentsJSON[] {
    return this.models.map((modelContents) => modelContents.dump());
  }

  static load(dumps: StickyModelContentsJSON[]): ModelContentsCollection {
    const entities = dumps.map((dump) => StickyModelContents.load(dump));
    return new ModelContentsCollection(entities);
  }

  cloneNew(
    assetUrlMap: AssetUrlMap,
    fileContainerAssetCopy?: FileContainerAssetCopy
  ): [ModelContentsCollection, Record<string, ModelKey>] {
    const models: StickyModelContents[] = [];
    const keyMap: Record<string, ModelKey> = {};
    this.models.forEach((model) => {
      const newModel = model.cloneNew(assetUrlMap, fileContainerAssetCopy);
      keyMap[model.key.toString()] = newModel.key;
      models.push(newModel);
    });
    return [new ModelContentsCollection(models), keyMap];
  }

  modelCollection(): ModelCollection {
    const entities = this.models.map((modelContents) => modelContents.modelEntity());
    return new ModelCollection(entities);
  }

  assetUrls(): string[] {
    const urls = this.list().reduce((prev, model) => {
      return [...prev, ...model.assetUrls()];
    }, [] as string[]);
    return Array.from(new Set(urls));
  }

  fileContainerAssetIds(): ViewModelAssetId[] {
    return Array.from(new Set(this.models.flatMap((model) => model.fileContainerAssetIds())));
  }
}
